import {
  countAbbrSyllables,
  hitTestKey,
  layoutQwerty,
  MAX_ABBR_INITIALS,
  MIN_ABBR_SYLLABLES,
  permuteAlphabet,
  type KeyId,
  type KeyRect,
  type Rect,
} from "@gaoya/shared";
import type { Session } from "../game/session.js";
import { isMyTurn, type UiState } from "../game/state.js";
import type { UiButton } from "./hall.js";

export function keysIn(area: Rect, letters?: string): KeyRect[] {
  return layoutQwerty(area, letters);
}

export function battleKeys(ui: UiState, area: Rect): KeyRect[] {
  const s = ui.selfScramble;
  if (s && s.turnsLeft > 0) return keysIn(area, permuteAlphabet(s.seed));
  return keysIn(area);
}

export function candidateButtons(
  area: Rect,
  words: readonly string[],
  hintWord: string | null = null,
): UiButton[] {
  if (words.length === 0) return [];
  const pad = 6;
  const n = words.length;
  const bw = (area.w - pad * (n + 1)) / n;
  const bh = Math.min(36, area.h - 4);
  const y = area.y + (area.h - bh) / 2;
  const out: UiButton[] = [];
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (!word) continue;
    out.push({
      id: `cand:${word}`,
      label: word,
      x: area.x + pad + i * (bw + pad),
      y,
      w: bw,
      h: bh,
      kind: hintWord === word ? "primary" : "wood",
    });
  }
  return out;
}

export function onKeyDown(ui: UiState, area: Rect, x: number, y: number, enabled: boolean): void {
  if (!enabled) return;
  const keys = battleKeys(ui, area);
  const id = hitTestKey(keys, x, y);
  if (!id) {
    ui.pressedKey = null;
    ui.popup = null;
    return;
  }
  const key = keys.find((k) => k.id === id);
  ui.pressedKey = String(id);
  if (key && id !== "BACKSPACE" && id !== "SUBMIT") {
    ui.popup = { label: key.label, x: key.x + key.w / 2, y: key.y };
  } else {
    ui.popup = null;
  }
}

export function onKeyMove(ui: UiState, area: Rect, x: number, y: number, enabled: boolean): void {
  if (!enabled || !ui.pointerDown) return;
  onKeyDown(ui, area, x, y, enabled);
}

export function onKeyUp(
  ui: UiState,
  session: Session,
  area: Rect,
  x: number,
  y: number,
  enabled: boolean,
  vibrate: () => void,
  playSfx?: () => void,
): void {
  const pressed = ui.pressedKey;
  ui.pressedKey = null;
  ui.popup = null;
  if (!enabled || !pressed) return;
  const id = hitTestKey(battleKeys(ui, area), x, y);
  if (!id) return;
  applyKey(ui, session, id);
  vibrate();
  playSfx?.();
}

export function applyKey(ui: UiState, session: Session, id: KeyId): void {
  if (id === "BACKSPACE") {
    ui.abbr = ui.abbr.slice(0, -1);
    ui.candidates = [];
    ui.lastQueriedAbbr = "";
    return;
  }
  if (id === "SUBMIT") {
    const first = ui.candidates[0];
    if (first) session.submitWord(first);
    else if (countAbbrSyllables(ui.abbr) >= MIN_ABBR_SYLLABLES) session.queryIfReady();
    return;
  }
  if (typeof id === "string" && id.length === 1) {
    if (ui.abbr.length >= MAX_ABBR_INITIALS) return;
    ui.abbr += id.toLowerCase();
    if (countAbbrSyllables(ui.abbr) >= MIN_ABBR_SYLLABLES) session.queryIfReady();
    else ui.candidates = [];
  }
}

export function keyboardEnabled(ui: UiState): boolean {
  return ui.screen === "battle" && isMyTurn(ui) && ui.authed && ui.connected;
}

export function handleCandidateTap(session: Session, id: string): boolean {
  if (!id.startsWith("cand:")) return false;
  const word = id.slice(5);
  if (word) session.submitWord(word);
  return true;
}
